import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, CircularProgress, Popover } from '@mui/material';
import { useTranslate } from 'src/locales';
import { ReactComponent as Element2Icon } from '../assets/element-2.svg';
import { ReactComponent as PillIcon } from '../assets/material-symbols_pill-outline.svg';
import { ReactComponent as StethoscopeIcon } from '../assets/material-symbols-light_stethoscope-outline-rounded.svg';
import { ReactComponent as BloodBagIcon } from '../assets/healthicons_blood-bag-outline.svg';
import { ReactComponent as DividerIcon } from '../assets/Divider.svg';
import { ReactComponent as CalendarDotIcon } from '../assets/CalendarDot.svg';
import { ReactComponent as CaretDownIcon } from '../assets/CaretDown.svg';
import { ByzMuiDatePicker } from 'src/components/core-components/mui/inputs/ByzMuiDatePicker';

interface GridMenuProps {
  selectedCategory?: string;
  selectedFilter?: string;
  selectedDate?: Date | null;
  onCategoryChange?: (category: string) => void;
  onFilterChange?: (filter: string) => void;
  onDateChange?: (date: Date | null) => void;
  isLoading?: boolean;
}

const GridMenu: React.FC<GridMenuProps> = ({
  selectedCategory = 'all',
  selectedFilter = 'all',
  selectedDate = new Date(),
  onCategoryChange,
  onFilterChange,
  onDateChange,
  isLoading = false,
}) => {
  const { t } = useTranslate();
  const [currentCategory, setCurrentCategory] = useState(selectedCategory);
  const [currentFilter, setCurrentFilter] = useState(selectedFilter);
  const [currentDate, setCurrentDate] = useState<Date | null>(selectedDate);
  const [dateAnchorEl, setDateAnchorEl] = useState<HTMLElement | null>(null);

  useEffect(() => {
    setCurrentDate(selectedDate);
  }, [selectedDate]);

  const handleCategoryClick = (category: string) => {
    setCurrentCategory(category);
    if (onCategoryChange) {
      onCategoryChange(category);
    }
  };

  const handleFilterClick = (filter: string) => {
    setCurrentFilter(filter);
    if (onFilterChange) {
      onFilterChange(filter);
    }
  };

  const handleDateChange = (date: Date | null) => {
    setCurrentDate(date);
    if (onDateChange) {
      onDateChange(date);
    }
    setDateAnchorEl(null);
  };

  const handlePreviousDay = () => {
    if (isLoading) return;
    if (currentDate) {
      const newDate = new Date(currentDate);
      newDate.setDate(newDate.getDate() - 1);
      handleDateChange(newDate);
    }
  };

  const handleNextDay = () => {
    if (isLoading) return;
    if (currentDate) {
      const newDate = new Date(currentDate);
      newDate.setDate(newDate.getDate() + 1);
      handleDateChange(newDate);
    }
  };

  return (
    <Box
      sx={{
        width: '100%',
        borderRadius: { xs: '12px', sm: '16px', md: '20px' },
        backgroundColor: '#fff',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        justifyContent: 'center',
        px: { xs: 1, sm: 1.5, md: 2.5 },
        py: 0,
      }}
    >
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          flexDirection: { xs: 'column', lg: 'row' },
          alignItems: { xs: 'stretch', lg: 'center' },
          gap: { xs: 1.5, sm: 2, md: 2.5 },
          py: 0,
          // Mobilde yatay scroll'u sadece alt bar'larda tut (aksi halde click/touch sapıtıyor)
          overflowX: 'visible',
        }}
      >
        {/* Categories */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: { xs: 1, sm: 1.5, md: 2.5 },
            overflowX: 'visible',
            flexWrap: { xs: 'wrap', lg: 'nowrap' },
            width: { xs: '100%', lg: 'auto' },
            flexShrink: 0,
          }}
        >
          {/* Tümü */}
          <Box
            onClick={() => handleCategoryClick('all')}
            sx={{
              borderBottom: '2px solid',
              borderColor: currentCategory === 'all' ? '#2979C3' : 'transparent',
              display: 'flex',
              alignItems: 'center',
              padding: { xs: '16px 0', sm: '20px 0', md: '24px 0' },
              gap: { xs: 0.5, sm: 0.75, md: 1 },
              cursor: 'pointer',
              color: currentCategory === 'all' ? '#2979C3' : '#687787',
              whiteSpace: 'nowrap',
              flexShrink: 0,
            }}
          >
            <Box
              component={Element2Icon}
              aria-hidden
              sx={{
                width: { xs: '18px', sm: '19px', md: '20px' },
                height: { xs: '18px', sm: '19px', md: '20px' },
              }}
            />
            <Typography
              sx={{
                fontSize: { xs: '12px', sm: '13px', md: '14px' },
                fontWeight: 600,
                lineHeight: '140%',
                color: 'inherit',
              }}
            >
              {t('nurse.order-medicine.categories.all')}
            </Typography>
          </Box>

          {/* İlaç Tedavileri */}
          <Box
            onClick={() => handleCategoryClick('medicine')}
            sx={{
              borderBottom: '2px solid',
              borderColor: currentCategory === 'medicine' ? '#2979C3' : 'transparent',
              display: 'flex',
              alignItems: 'center',
              padding: { xs: '16px 0', sm: '20px 0', md: '24px 0' },
              gap: { xs: 0.5, sm: 0.75, md: 1 },
              cursor: 'pointer',
              color: currentCategory === 'medicine' ? '#2979C3' : '#687787',
              whiteSpace: 'nowrap',
              flexShrink: 0,
            }}
          >
            <Box
              component={PillIcon}
              aria-hidden
              sx={{
                width: { xs: '18px', sm: '19px', md: '20px' },
                height: { xs: '18px', sm: '19px', md: '20px' },
              }}
            />
            <Typography
              sx={{
                fontSize: { xs: '12px', sm: '13px', md: '14px' },
                fontWeight: 600,
                lineHeight: '140%',
                color: 'inherit',
              }}
            >
              {t('nurse.order-medicine.categories.medicine')}
            </Typography>
          </Box>

          {/* Diğer Tedaviler */}
          <Box
            onClick={() => handleCategoryClick('other')}
            sx={{
              borderBottom: '2px solid',
              borderColor: currentCategory === 'other' ? '#2979C3' : 'transparent',
              display: 'flex',
              alignItems: 'center',
              padding: { xs: '16px 0', sm: '20px 0', md: '24px 0' },
              gap: { xs: 0.5, sm: 0.75, md: 1 },
              cursor: 'pointer',
              color: currentCategory === 'other' ? '#2979C3' : '#687787',
              whiteSpace: 'nowrap',
              flexShrink: 0,
            }}
          >
            <Box
              component={StethoscopeIcon}
              aria-hidden
              sx={{
                width: { xs: '20px', sm: '22px', md: '24px' },
                height: { xs: '20px', sm: '22px', md: '24px' },
              }}
            />
            <Typography
              sx={{
                fontSize: { xs: '12px', sm: '13px', md: '14px' },
                fontWeight: 600,
                lineHeight: '140%',
                color: 'inherit',
              }}
            >
              {t('nurse.order-medicine.categories.other')}
            </Typography>
          </Box>

          {/* Kan İstekleri */}
          <Box
            onClick={() => handleCategoryClick('blood')}
            sx={{
              borderBottom: '2px solid',
              borderColor: currentCategory === 'blood' ? '#2979C3' : 'transparent',
              display: 'flex',
              alignItems: 'center',
              padding: { xs: '16px 0', sm: '20px 0', md: '24px 0' },
              gap: { xs: 0.5, sm: 0.75, md: 1 },
              cursor: 'pointer',
              color: currentCategory === 'blood' ? '#2979C3' : '#687787',
              whiteSpace: 'nowrap',
              flexShrink: 0,
            }}
          >
            <Box
              component={BloodBagIcon}
              aria-hidden
              sx={{
                width: { xs: '18px', sm: '19px', md: '20px' },
                height: { xs: '18px', sm: '19px', md: '20px' },
              }}
            />
            <Typography
              sx={{
                fontSize: { xs: '12px', sm: '13px', md: '14px' },
                fontWeight: 600,
                lineHeight: '140%',
                color: 'inherit',
              }}
            >
              {t('nurse.order-medicine.categories.blood')}
            </Typography>
          </Box>
        </Box>

        {/* Divider */}
        <Box
          component={DividerIcon}
          aria-hidden
          sx={{
            height: { xs: '24px', sm: '26px', md: '28px' },
            width: '1px',
            display: { xs: 'none', lg: 'block' },
          }}
        />

        {/* Filter Buttons */}
        <Box
          sx={{
            borderRadius: { xs: '6px', sm: '7px', md: '8px' },
            backgroundColor: '#FAFAFA',
            border: '1px solid #ECEFF2',
            display: 'flex',
            alignItems: 'flex-start',
            padding: { xs: '2px', sm: '2.5px', md: '3px' },
            gap: 0,
            width: { xs: '100%', lg: 'auto' },
            overflowX: 'visible',
            flexWrap: { xs: 'wrap', lg: 'nowrap' },
            flexShrink: 0,
          }}
        >
          {/* Tümü Filter */}
          <Button
            onClick={() => handleFilterClick('all')}
            sx={{
              color: currentFilter === 'all' ? '#3C4551' : '#8A97A6',
              fontSize: { xs: '11px', sm: '11.5px', md: '12px' },
              fontWeight: currentFilter === 'all' ? 600 : 500,
              px: { xs: 0.75, sm: 0.875, md: 1 },
              py: { xs: 0.375, sm: 0.4375, md: 0.5 },
              minWidth: { xs: 'calc(50% - 2px)', sm: '70px', md: '80px' },
              textTransform: 'none',
              letterSpacing: '-0.02em',
              lineHeight: '140%',
              whiteSpace: 'nowrap',
              flexShrink: 0,
              '&:hover': {
                backgroundColor: 'transparent',
              },
            }}
          >
            {t('nurse.order-medicine.filters.all')}
          </Button>

          {/* Beklemede */}
          <Button
            onClick={() => handleFilterClick('pending')}
            sx={{
              color: currentFilter === 'pending' ? '#3C4551' : '#8A97A6',
              fontSize: { xs: '11px', sm: '11.5px', md: '12px' },
              fontWeight: currentFilter === 'pending' ? 600 : 500,
              px: { xs: 0.75, sm: 0.875, md: 1 },
              py: { xs: 0.375, sm: 0.4375, md: 0.5 },
              minWidth: { xs: 'calc(50% - 2px)', sm: '65px', md: '75px' },
              textTransform: 'none',
              letterSpacing: '-0.02em',
              lineHeight: '140%',
              whiteSpace: 'nowrap',
              flexShrink: 0,
              '&:hover': {
                backgroundColor: 'transparent',
              },
            }}
          >
            {t('nurse.order-medicine.filters.pending')}
          </Button>

          {/* Geçenler */}
          <Button
            onClick={() => handleFilterClick('past')}
            sx={{
              color: currentFilter === 'past' ? '#3C4551' : '#8A97A6',
              fontSize: { xs: '11px', sm: '11.5px', md: '12px' },
              fontWeight: currentFilter === 'past' ? 600 : 500,
              px: { xs: 0.75, sm: 0.875, md: 1 },
              py: { xs: 0.375, sm: 0.4375, md: 0.5 },
              minWidth: { xs: 'calc(50% - 2px)', sm: '60px', md: '70px' },
              textTransform: 'none',
              letterSpacing: '-0.02em',
              lineHeight: '140%',
              whiteSpace: 'nowrap',
              flexShrink: 0,
              '&:hover': {
                backgroundColor: 'transparent',
              },
            }}
          >
            {t('nurse.order-medicine.filters.past')}
          </Button>

          {/* Uygulananlar */}
          <Button
            onClick={() => handleFilterClick('applied')}
            sx={{
              color: currentFilter === 'applied' ? '#3C4551' : '#8A97A6',
              fontSize: { xs: '11px', sm: '11.5px', md: '12px' },
              fontWeight: currentFilter === 'applied' ? 600 : 500,
              px: { xs: 0.75, sm: 0.875, md: 1 },
              py: { xs: 0.375, sm: 0.4375, md: 0.5 },
              minWidth: { xs: 'calc(50% - 2px)', sm: '65px', md: '75px' },
              textTransform: 'none',
              letterSpacing: '-0.02em',
              lineHeight: '140%',
              whiteSpace: 'nowrap',
              flexShrink: 0,
              '&:hover': {
                backgroundColor: 'transparent',
              },
            }}
          >
            {t('nurse.order-medicine.filters.applied')}
          </Button>
        </Box>

        {/* Divider */}
        <Box
          component={DividerIcon}
          aria-hidden
          sx={{
            height: { xs: '24px', sm: '26px', md: '28px' },
            width: '1px',
            display: { xs: 'none', lg: 'block' },
          }}
        />

        {/* Date Selector */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: { xs: 1, sm: 1.25, md: 1.5 },
            fontSize: { xs: '11px', sm: '11.5px', md: '12px' },
            color: '#545A66',
            fontFamily: 'Plus Jakarta Sans',
            width: { xs: '100%', lg: 'auto' },
            justifyContent: { xs: 'center', lg: 'flex-start' },
            flexShrink: 0,
          }}
        >
          {/* Previous Day Button (Geri) */}
          <Box
            onClick={handlePreviousDay}
            sx={{
              width: { xs: '28px', sm: '30px', md: '32px' },
              height: { xs: '28px', sm: '30px', md: '32px' },
              borderRadius: { xs: '6px', sm: '7px', md: '8px' },
              backgroundColor: '#ffffff',
              border: '1px solid #F0F0F0',
              boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.05)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: isLoading ? 'not-allowed' : 'pointer',
              flexShrink: 0,
              opacity: isLoading ? 0.6 : 1,
              '&:hover': {
                backgroundColor: isLoading ? '#ffffff' : '#545A66',
                '& svg': isLoading
                  ? {}
                  : {
                    filter: 'brightness(0) invert(1)', // İkonu beyaz yap
                  },
              },
            }}
          >
            <Box
              component={CaretDownIcon}
              aria-hidden
              sx={{
                width: { xs: '14px', sm: '15px', md: '16px' },
                height: { xs: '14px', sm: '15px', md: '16px' },
                transform: 'rotate(180deg)', // Sol için 180 derece döndür
              }}
            />
          </Box>
          {/* Date Display */}
          <Box
            sx={{
              height: { xs: '28px', sm: '30px', md: '32px' },
              boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.05)',
              borderRadius: { xs: '6px', sm: '7px', md: '8px' },
              backgroundColor: '#fff',
              border: '1px solid #F0F0F0',
              boxSizing: 'border-box',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              px: { xs: 1, sm: 1.25, md: 1.5 },
              gap: { xs: 0.5, sm: 0.75, md: 1 },
              cursor: isLoading ? 'default' : 'pointer',
              flex: { xs: '1 1 auto', lg: '0 0 auto' },
              minWidth: { xs: 'auto', sm: '120px', md: '140px' },
            }}
            onClick={(event) => {
              if (isLoading) return;
              setDateAnchorEl(event.currentTarget as HTMLElement);
            }}
          >
            <Box
              component={CalendarDotIcon}
              aria-hidden
              sx={{
                width: { xs: '14px', sm: '15px', md: '16px' },
                height: { xs: '14px', sm: '15px', md: '16px' },
              }}
            />
            <Typography
              sx={{
                fontSize: { xs: '11px', sm: '11.5px', md: '12px' },
                letterSpacing: '-0.02em',
                color: '#545A66',
                fontFamily: 'Plus Jakarta Sans',
                whiteSpace: 'nowrap',
              }}
            >
              {currentDate
                ? new Date(currentDate).toLocaleDateString('tr-TR', {
                  day: '2-digit',
                  month: '2-digit',
                  year: 'numeric',
                })
                : '02.12.2025'}
            </Typography>
            {isLoading && (
              <CircularProgress
                size={16}
                sx={{
                  color: '#2979C3',
                }}
              />
            )}
          </Box>
          {/* Next Day Button (İleri) */}
          <Box
            onClick={handleNextDay}
            sx={{
              width: { xs: '28px', sm: '30px', md: '32px' },
              height: { xs: '28px', sm: '30px', md: '32px' },
              borderRadius: { xs: '6px', sm: '7px', md: '8px' },
              backgroundColor: '#ffffff',
              border: '1px solid #F0F0F0',
              boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.05)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: isLoading ? 'not-allowed' : 'pointer',
              flexShrink: 0,
              opacity: isLoading ? 0.6 : 1,
              '&:hover': {
                backgroundColor: isLoading ? '#ffffff' : '#545A66',
                '& svg': isLoading
                  ? {}
                  : {
                    filter: 'brightness(0) invert(1)', // İkonu beyaz yap
                  },
              },
            }}
          >
            <Box
              component={CaretDownIcon}
              aria-hidden
              sx={{
                width: { xs: '14px', sm: '15px', md: '16px' },
                height: { xs: '14px', sm: '15px', md: '16px' },
                transform: 'rotate(360deg)', // Sağ için 360 derece döndür
              }}
            />
          </Box>
        </Box>
      </Box>
      <Popover
        open={Boolean(dateAnchorEl)}
        anchorEl={dateAnchorEl}
        onClose={() => setDateAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Box sx={{ p: 2 }}>
          <ByzMuiDatePicker
            value={currentDate}
            onChange={(value) => {
              const dateValue = value ? new Date(value as any) : null;
              handleDateChange(dateValue);
            }}
            slotProps={{
              textField: {
                label: '',
              },
            }}
          />
        </Box>
      </Popover>
    </Box>
  );
};

export default GridMenu;

